import { Pressable, Text } from '@vidahealth/vida-ui';
import { cssInterop } from 'nativewind';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
  type TextInput,
} from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { useScreenLayout } from '#modules/ui/Screen';
import { TextField } from '#modules/ui/TextField';

// Reanimated's `Animated.View` isn't pre-wired into nativewind, so without
// this the `className` prop is dropped and the dropdown renders without its
// border, background, rounding, etc.
cssInterop(Animated.View, { className: 'style' });

type KeyPressEvent = Parameters<
  NonNullable<React.ComponentProps<typeof TextInput>['onKeyPress']>
>[0];

type AutocompleteProps<T> = {
  /** Current input text — controlled by the parent. */
  value: string;
  onChangeText: (text: string) => void;
  /**
   * Fired after the input loses focus (and after the brief delay that lets a
   * click-to-select register first). Use it to reconcile the typed text with a
   * confirmed selection — e.g. clear a partial query the user never picked.
   */
  onBlur?: () => void;
  /** Options to display in the dropdown. The parent is responsible for filtering. */
  options: T[];
  onSelect: (option: T) => void;
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  placeholder?: string;
  label?: ReactNode;
  /** Slot rendered inside <Input> before the InputField (e.g. a search icon). */
  startSlot?: ReactNode;
  /** Slot rendered inside <Input> after the InputField. */
  endSlot?: ReactNode;
  /**
   * Shown inside the dropdown when value is non-empty but options is empty.
   * Omit to hide the dropdown entirely when there are no matches (the typical
   * "feels quiet, not chatty" UX).
   */
  emptyMessage?: string;
  /** Error message displayed below the input. */
  errorMessage?: string;
  /**
   * When true, opens the dropdown and renders a loading row. Use for the
   * initial data-source fetch — for per-keystroke "processing" feedback,
   * pass a spinner through `endSlot` instead so the menu doesn't churn.
   */
  loading?: boolean;
  /** Copy shown inside the loading row; defaults to a generic "Loading…". */
  loadingMessage?: string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  testID?: string;
};

// Floor so the dropdown is still usable on small viewports even when a tall
// sticky footer would otherwise compress it to a few pixels.
const MIN_DROPDOWN_HEIGHT = 120;

export function Autocomplete<T>({
  value,
  onChangeText,
  onBlur,
  options,
  onSelect,
  getOptionKey,
  getOptionLabel,
  placeholder,
  label,
  startSlot,
  endSlot,
  emptyMessage,
  errorMessage,
  loading = false,
  loadingMessage = 'Loading…',
  autoCapitalize = 'words',
  testID,
}: AutocompleteProps<T>) {
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownTop, setDropdownTop] = useState<number | null>(null);
  // Blur fires before option onPress on web. Delaying close lets the press
  // register; the ref tracks the timeout so a re-focus can cancel it.
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<View>(null);
  // The blur timeout (below) may outlive a click-to-select that re-renders the
  // parent, so call the LATEST onBlur via a ref — a closure captured at blur
  // time would still see the pre-selection props and clear a just-picked value.
  const onBlurRef = useRef(onBlur);

  const isWeb = Platform.OS === 'web';
  const hasQuery = value.trim().length > 0;
  // Dropdown opens when there's something useful to show: matches, an active
  // loading state, or a deliberate empty-state message from the consumer.
  // Otherwise it stays hidden (no "no matches" chatter by default).
  const isOpen = isFocused && hasQuery && (loading || options.length > 0 || !!emptyMessage);

  const { height: windowHeight } = useWindowDimensions();
  const { scrollContentPaddingBottom } = useScreenLayout();

  // Cap the dropdown so its inline height never pushes the Screen's
  // ScrollView contents past the viewport (which would surface a page
  // scrollbar). Screen's `scrollContentPaddingBottom` already accounts for
  // the sticky footer's height + the visual breathing-room gap, so
  // subtracting it lands the dropdown's bottom edge right at that line.
  const maxHeight = useMemo(() => {
    if (dropdownTop === null) return undefined;
    return Math.max(MIN_DROPDOWN_HEIGHT, windowHeight - dropdownTop - scrollContentPaddingBottom);
  }, [dropdownTop, windowHeight, scrollContentPaddingBottom]);

  const handleSelect = useCallback(
    (option: T) => {
      onSelect(option);
      setHighlightedIndex(-1);
    },
    [onSelect]
  );

  const handleChange = useCallback(
    (text: string) => {
      onChangeText(text);
      setHighlightedIndex(-1);
    },
    [onChangeText]
  );

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setHighlightedIndex(-1);
      onBlurRef.current?.();
    }, 150);
  };

  const handleKeyPress = (e: KeyPressEvent) => {
    if (!isWeb) return;
    const key = e.nativeEvent.key;
    if (key === 'ArrowDown') {
      e.preventDefault?.();
      if (options.length === 0) return;
      setHighlightedIndex((i) => (i + 1) % options.length);
    } else if (key === 'ArrowUp') {
      e.preventDefault?.();
      if (options.length === 0) return;
      setHighlightedIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (key === 'Enter') {
      // Prefer the highlighted option (user arrowed/hovered to a choice).
      // Fall back to an exact-label match against the typed text so that
      // typing the full org name + Enter selects without forcing the user
      // to also reach for the arrow keys or mouse.
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        e.preventDefault?.();
        handleSelect(options[highlightedIndex]);
      } else {
        const trimmed = value.trim().toLowerCase();
        const exactMatch = options.find((o) => getOptionLabel(o).trim().toLowerCase() === trimmed);
        if (exactMatch) {
          e.preventDefault?.();
          handleSelect(exactMatch);
        }
      }
    } else if (key === 'Escape') {
      setIsFocused(false);
      setHighlightedIndex(-1);
    }
  };

  const activeDescendantId = useMemo(() => {
    if (highlightedIndex < 0 || highlightedIndex >= options.length) return undefined;
    return `${testID ?? 'autocomplete'}-option-${getOptionKey(options[highlightedIndex])}`;
  }, [highlightedIndex, options, getOptionKey, testID]);

  const measureDropdownTop = useCallback(() => {
    const node = dropdownRef.current;
    if (!node) return;
    if (Platform.OS === 'web') {
      // RN Web's View ref forwards to the underlying DOM element.
      const rect = (node as unknown as HTMLElement).getBoundingClientRect?.();
      if (rect) setDropdownTop(rect.top);
      return;
    }
    (
      node as unknown as { measureInWindow?: (cb: (x: number, y: number) => void) => void }
    ).measureInWindow?.((_x, y) => {
      if (typeof y === 'number') setDropdownTop(y);
    });
  }, []);

  // Keep the ref pointing at the current onBlur so the blur timeout always
  // invokes the latest handler (see the ref declaration above).
  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  // onLayout doesn't fire reliably when a conditional View first mounts on web,
  // so measure here whenever the dropdown opens (or the window resizes).
  useEffect(() => {
    if (!isOpen) return;
    measureDropdownTop();
  }, [isOpen, windowHeight, measureDropdownTop]);

  return (
    <View>
      <TextField
        value={value}
        onChangeText={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        autoCapitalize={autoCapitalize}
        autoCorrect={false}
        placeholder={placeholder}
        label={label}
        startSlot={startSlot}
        endSlot={endSlot}
        // Tighten the gap between a leading search icon and the typed query.
        // vida-ui resolves the InputField's intrinsic horizontal padding via
        // an inline style array, so the override has to go through `style`
        // rather than `className`. Only applies when there's actually a
        // startSlot to butt up against.
        inputFieldStyle={startSlot ? { paddingLeft: 4 } : undefined}
        errorMessage={errorMessage}
        testID={testID}
        // Web-only ARIA props for combobox semantics. RN ignores unknown
        // props, so this is safe across platforms.
        {...(isWeb
          ? ({
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': isOpen,
              'aria-activedescendant': activeDescendantId,
            } as object)
          : {})}
      />

      {isOpen ? (
        <Animated.View
          ref={dropdownRef}
          onLayout={measureDropdownTop}
          entering={FadeIn.duration(160)}
          exiting={FadeOut.duration(120)}
          className='mt-2 overflow-hidden rounded-vui-sm border border-outline-200 bg-background-0'
          style={maxHeight !== undefined ? { maxHeight } : undefined}
        >
          {loading && options.length === 0 ? (
            <View
              className='flex-row items-center gap-3 px-4 py-3'
              accessibilityRole='progressbar'
              accessibilityLabel={loadingMessage}
              testID={testID ? `${testID}-loading` : undefined}
            >
              <ActivityIndicator size='small' />
              <Text size='sm' className='text-typography-600'>
                {loadingMessage}
              </Text>
            </View>
          ) : options.length === 0 && emptyMessage ? (
            <Text size='sm' className='px-4 py-3 text-typography-600'>
              {emptyMessage}
            </Text>
          ) : (
            // Plain ScrollView + .map() instead of FlatList: results are capped
            // by the consumer (MAX_RESULTS-style), so virtualization buys
            // nothing, and a VirtualizedList inside the Screen's ScrollView
            // triggers RN's "same orientation" warning on iOS.
            <ScrollView
              keyboardShouldPersistTaps='handled'
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              {options.map((item, index) => {
                const key = getOptionKey(item);
                const isHighlighted = index === highlightedIndex;
                return (
                  <Pressable
                    key={key}
                    nativeID={`${testID ?? 'autocomplete'}-option-${key}`}
                    className={`px-4 py-3 ${isHighlighted ? 'bg-background-100' : 'active:bg-background-100'}`}
                    onPress={() => handleSelect(item)}
                    onHoverIn={() => setHighlightedIndex(index)}
                    accessibilityRole='button'
                    testID={testID ? `${testID}-option-${key}` : undefined}
                  >
                    <Text size='md' className='text-typography-900'>
                      {getOptionLabel(item)}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </Animated.View>
      ) : null}
    </View>
  );
}
